import {
  Box,
  Card,
  Flex,
  HStack,
  IconButton,
  Text,
  VStack,
  useDisclosure,
} from '@chakra-ui/react'
import { QueryNodeUtils } from '@helo/cms'
import { pipe } from '@helo/utils'
import { Link } from 'react-router-dom'

import { SavedViewWithLabel } from '../../pages/SavedViewsPage/SavedViewsPage.parts'
import { useAppStore } from '../../store/appStore/appStore'
import { colors } from '../../theme/colors'
import { ExportModal } from '../ExportModal/ExportModal'
import { Icon } from '../Icons/Icon'
import { showToast } from '../Toast/showToast'

export interface SavedViewCardProps {
  savedView: SavedViewWithLabel
}

const homeDashboardAccentTeal = colors.page.light.homeDashboard.accentTeal
const homeDashboardMintPanel = colors.page.light.homeDashboard.mintPanel

const savedViewActionIconButtonProps = {
  variant: 'ghost' as const,
  color: homeDashboardAccentTeal,
  _hover: {
    bg: 'presets.neutral.20',
    color: homeDashboardAccentTeal,
  },
  _active: {
    bg: 'presets.neutral.20',
    color: homeDashboardAccentTeal,
  },
}

const capitalizeFirstValidLetter = (str: string) => {
  if (!str) return str

  const firstValidLetterIndex = str.search(/[a-zA-Z]/)
  if (firstValidLetterIndex === -1) return str

  return (
    str.slice(0, firstValidLetterIndex) +
    str.charAt(firstValidLetterIndex).toUpperCase() +
    str.slice(firstValidLetterIndex + 1)
  )
}

export const SavedViewCard = ({ savedView }: SavedViewCardProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const route = useAppStore.getState().router.getRouteById(savedView.viewId)
  if (!route) return null
  const { description, name, viewLabel, viewState, tableConfig } = savedView

  // Get queryNode for queryDefinitionId and queryVariables
  const queryNode = tableConfig?.queryId
    ? useAppStore.getState().queryStore.getQuery(tableConfig.queryId)
    : undefined

  const queryDefinitionId =
    queryNode?.queryDefinition?.id ?? tableConfig?.queryId

  const queryVariables = queryNode
    ? pipe(queryNode, QueryNodeUtils.getMappedVariablesAsObject())
    : (() => {
        // Fallback to variables from saved view's queryStates
        const matchingQueryState = viewState?.queryStates?.find(
          qs => qs.queryId === tableConfig?.queryId,
        )
        return matchingQueryState?.variables || {}
      })()

  // Only allow export if we have a queryDefinitionId
  const canExport = Boolean(queryDefinitionId)

  const searchParams = new URLSearchParams([['savedViewId', savedView.id]])
  const url = `${route.path}?${searchParams.toString()}`

  const handleShare = async () => {
    const link = `${window.location.origin}${url}`
    try {
      await navigator.clipboard.writeText(link)
      showToast({
        title: 'Share view link',
        description: 'The link was copied to your clipboard.',
        icon: 'CopyPasteIcon',
      })
    } catch (err) {
      console.error(err)
      showToast({
        title: 'Share view link',
        description: 'Unable to copy link to your clipboard.',
        icon: 'CopyPasteIcon',
        variant: 'error',
      })
    }
  }

  return (
    <Link
      to={url}
      target="_blank"
      rel="noopener noreferrer"
      style={{ textDecoration: 'none' }}
    >
      <Card
        borderRadius="12px"
        border="1px solid"
        borderColor="presets.neutral.20"
        backgroundColor="white"
        height="72px"
        position="relative"
        overflow="hidden"
        direction="row"
        _hover={{
          borderColor: homeDashboardAccentTeal,
          cursor: 'pointer',
        }}
        _dark={{
          borderColor: 'transparent',
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          _hover: {
            borderColor: homeDashboardAccentTeal,
            cursor: 'pointer',
          },
        }}
      >
        <HStack
          alignItems="center"
          backgroundColor={homeDashboardAccentTeal}
          width="38%"
          gap="19px"
          overflow="hidden"
          _dark={{
            backgroundColor: 'rgba(255, 255, 255, 0.10)',
          }}
        >
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            bg="presets.neutral.0"
            borderRadius="full"
            boxSize="44px"
            ml="20px"
            flexShrink={0}
          >
            <Icon
              icon={route.routeIconName!}
              boxSize="20px"
              color={homeDashboardAccentTeal}
            />
          </Box>
          <VStack gap="7px" alignItems="stretch" mr="50px">
            <Text
              color="white"
              fontSize="14px"
              fontWeight="600"
              noOfLines={1}
              _dark={{ color: 'presets.turquoise.30' }}
            >
              {name}
            </Text>
            <Text
              color="white"
              fontSize="12px"
              fontWeight="500"
              _dark={{ color: 'presets.neutral.0' }}
            >
              {viewLabel}
            </Text>
          </VStack>
        </HStack>
        <Flex
          alignItems="center"
          width="62%"
          justifyContent="space-between"
          backgroundColor={homeDashboardMintPanel}
          _dark={{ backgroundColor: 'transparent' }}
        >
          <Box ml="42px" textOverflow="ellipsis" overflow="hidden">
            <Text
              fontSize="14px"
              color="presets.neutral.90"
              fontWeight="500"
              overflow="hidden"
              noOfLines={2}
              _dark={{ color: 'presets.neutral.0' }}
            >
              {capitalizeFirstValidLetter(description) || 'No description'}
            </Text>
          </Box>
          <HStack gap="10px" mr="20px" ml="20px">
            {tableConfig && (
              <Box
                onClick={e => {
                  e.preventDefault()
                  e.stopPropagation()
                }}
              >
                <ExportModal
                  queryId={tableConfig.queryId}
                  title={name}
                  exportTitle={name}
                  isOpen={isOpen}
                  onOpen={canExport ? onOpen : undefined}
                  onClose={onClose}
                  queryVariables={queryVariables}
                  queryDefinitionId={queryDefinitionId}
                  columns={tableConfig?.columns}
                  columnConfigs={tableConfig?.columnConfigs}
                  connectionLocator={tableConfig?.connectionLocator}
                  isDisabled={!canExport}
                  renderTrigger={
                    <IconButton
                      {...savedViewActionIconButtonProps}
                      aria-label="Export"
                      icon={<Icon icon="DownloadIcon" boxSize="20px" />}
                      onClick={onOpen}
                    />
                  }
                />
              </Box>
            )}
            <IconButton
              {...savedViewActionIconButtonProps}
              onClick={e => {
                e.preventDefault()
                e.stopPropagation()
                handleShare()
              }}
              aria-label="Share"
              icon={<Icon icon="ShareIcon" boxSize="20px" />}
            />
          </HStack>
        </Flex>
      </Card>
    </Link>
  )
}
